/* eslint-disable eqeqeq */
import { resolveBoundedCommit } from '../formUtils';
import type { AccessoryFormApi } from './useAccessoryForm';
import type { InternalWallOption } from './internalWalls';

/**
 * Phase 6e2b-2 — the COMPLEX-type mounting block (damper/louver/hood/opening/
 * access_door form tmpls): the per-plane two-row distance grammar incl. the
 * internal-wall vertical/horizontal rows and the mixed on_cabinet+IW plane
 * select. 6e2b-1's MountingSection covers the simpler census variants; these
 * five tmpls add IW options, hood entries (damper) and per-type gating —
 * grammar copied row-for-row from the sampled tmpls (§52.1).
 */

const fix2 = (v: unknown): string => {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : String(v);
};

export type ComplexVariant = 'damper' | 'louver' | 'hood' | 'opening' | 'access_door';

interface RowSpec {
  axis: 'x' | 'y';
  compName: string;
  compField: string;
  label: [string, string];
}

const PLANE_ROWS: Record<string, [RowSpec, RowSpec]> = {
  roof: [
    { axis: 'y', compName: 'position_Wy', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  floor: [
    { axis: 'y', compName: 'position_Wy', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  wall_downstream: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Wx', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
  ],
  wall_left: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  wall_right: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Lx', compField: 'distance_to_downstream_wall', label: ['To Wall Ups', 'Downs'] },
  ],
  wall_upstream: [
    { axis: 'y', compName: 'position_Hy', compField: 'distance_to_roof', label: ['To Floor', 'Roof'] },
    { axis: 'x', compName: 'position_Wx', compField: 'distance_to_right_wall', label: ['To Wall Left', 'Right'] },
  ],
};

const PositionInput = ({
  api,
  name,
  value,
  readOnlyGated,
  extraClass = 'medium-input',
  tabMinus,
  min,
}: {
  api: AccessoryFormApi;
  name: string;
  value: unknown;
  readOnlyGated: boolean;
  extraClass?: string;
  tabMinus?: boolean;
  /** enforced lower bound (access door To Floor — accessDoorFloorClearance). */
  min?: number | null;
}) => {
  /** One commit path for blur and Enter — see `resolveBoundedCommit`, which is
   * shared with the mounting popup's twin of this box. */
  const commit = (el: HTMLInputElement) => {
    if (readOnlyGated) return;
    const next = resolveBoundedCommit(el, fix2(value), min);
    if (next != null) api.commitPositionField(name, next);
  };
  return (
    <input
      type="text"
      data-formatter_type="decimal"
      autoComplete="off"
      data-min={min != null ? String(min) : '0'}
      name={name}
      className={`position_field ${extraClass}`}
      readOnly={readOnlyGated}
      tabIndex={tabMinus ? -1 : undefined}
      defaultValue={fix2(value)}
      key={`${name}_${fix2(value)}`}
      onBlur={(e) => commit(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(e.currentTarget);
      }}
    />
  );
};

const AutoPositionCheckbox = ({
  api,
  axis,
}: {
  api: AccessoryFormApi;
  axis: 'x' | 'y';
}) => {
  const name = axis === 'y' ? 'default_pos_y' : 'default_pos_x';
  const checked = !!api.position?.get(name);
  return (
    <label className="for-center-position">
      {/* §JSX-WS — the `{' '}` is load-bearing. Every accessory form.tmpl puts a
          NEWLINE between the checkbox and `<span>Auto-Position</span>` (opening
          form.tmpl:137-139, and damper/louver/hood/light/jbox/… identically),
          so legacy renders a real ~3.2px space on top of the 5px
          `.for-center-position span` padding (ahu/styles.css:1554). JSX drops
          whitespace containing a newline, so the caption butted against the box
          and — the label being `float: right` (:1549) — the whole
          checkbox+caption pair sat 3.24px right of legacy (measured 422.47 vs
          419.23 on the Opening Mounting rows). */}
      <input
        type="checkbox"
        name={name}
        className="position_field"
        checked={checked}
        onChange={(e) => api.commitPositionCheckbox(name, e.target.checked)}
      />{' '}
      <span>Auto-Position</span>
    </label>
  );
};

/**
 * The per-plane two-row distance block + IW rows. `planeValue` is the
 * resolved cms plane value (on_cabinet) — IW rows trigger off `iwPlane`
 * ('vertical'/'horizontal', position.mounting_plane.plane in the tmpls).
 */
export const ComplexPlaneRows = ({
  api,
  variant,
  planeValue,
  iwPlane,
  floorMin,
}: {
  api: AccessoryFormApi;
  variant: ComplexVariant;
  planeValue: string | null;
  iwPlane: string | null;
  /**
   * Lower bound for the "To Floor" box (the `y` row of the four wall planes).
   * Access door only — `useAccessDoorFloorMinimum` mirrors the server's
   * `min_clear_from_floor`; the other four variants pass nothing and keep the
   * unbounded box they have always had.
   */
  floorMin?: number | null;
}) => {
  const pos = api.position;
  const model = api.model;
  if (!pos || !model || !pos.get('mounting_plane_id')) return null;
  const hoodBound = variant === 'damper' && !!model.get('hood_id');
  const liClass = hoodBound ? 'non-clickable' : undefined;
  const hasCheckbox = variant !== 'access_door';
  const gated = (axis: 'x' | 'y') =>
    variant === 'access_door'
      ? false
      : !!pos.get(axis === 'y' ? 'default_pos_y' : 'default_pos_x');

  if (iwPlane === 'vertical' || iwPlane === 'horizontal') {
    // access_door has only the vertical block (tmpl census); others both.
    if (variant === 'access_door' && iwPlane !== 'vertical') return null;
    const rows =
      iwPlane === 'vertical'
        ? ([
            { axis: 'y' as const, label: 'To Bottom Edge' },
            { axis: 'x' as const, label: 'To Left Edge' },
          ] as const)
        : ([
            { axis: 'x' as const, label: 'To Upstream Edge' },
            { axis: 'y' as const, label: 'To Left Edge' },
          ] as const);
    const iwInputClass =
      variant === 'access_door' ? '' : variant === 'opening' && iwPlane === 'horizontal' ? '' : 'large-input';
    return (
      <>
        {rows.map((r) => (
          <li key={r.axis}>
            <div className="accessory-mounting-floor">
              {/* §JSX-WS — see the two-input row below: the newline the tmpls
                  leave after `</span>` is a real ~4px space, and `.unit` is
                  inline-block so the input starts after it. */}
              <span className="unit">in</span>{' '}
              <PositionInput
                api={api}
                name={`position_${r.axis}`}
                value={pos.get(r.axis)}
                readOnlyGated={gated(r.axis)}
                extraClass={iwInputClass}
                tabMinus
              />
              {hasCheckbox && <AutoPositionCheckbox api={api} axis={r.axis} />}
              <span className="label">
                <span>{r.label}</span>
              </span>
            </div>
          </li>
        ))}
      </>
    );
  }

  const rows = planeValue ? PLANE_ROWS[planeValue] : undefined;
  if (!rows) return null;
  return (
    <>
      {rows.map((spec) => (
        <li key={spec.axis} className={liClass}>
          <div className="accessory-mounting-floor">
            {/* §JSX-WS — the two `{' '}`s are load-bearing. `.unit` is
                inline-block (ahu/styles.css `.accessory .unit`) and both boxes
                are `.medium-input`, so the NEWLINES the tmpls leave between
                `</span>`, the editable input and the readonly companion render
                as two real ~4px spaces (access_door form.tmpl:204-212 and the
                damper/louver/hood/opening equivalents). JSX drops whitespace
                containing a newline, so the pair sat 3.98 / 7.97px left of
                legacy. No space is added before the floated `.for-center-position`
                checkbox or the floated `.label` — legacy has a newline in both
                places and neither renders (measured: `.label` left is 49.98 in
                both). */}
            <span className="unit">in</span>{' '}
            <PositionInput
              api={api}
              name={`position_${spec.axis}`}
              value={pos.get(spec.axis)}
              readOnlyGated={gated(spec.axis)}
              min={spec.axis === 'y' ? floorMin : null}
            />{' '}
            <input
              type="text"
              data-formatter_type="decimal"
              autoComplete="off"
              data-min="0"
              name={spec.compName}
              className="position_field medium-input"
              value={fix2(model.get(spec.compField))}
              readOnly
              tabIndex={-1}
              onChange={() => undefined}
            />
            {hasCheckbox && <AutoPositionCheckbox api={api} axis={spec.axis} />}
            <span className="label">
              <span>{spec.label[0]}</span> | <span>{spec.label[1]}</span>
            </span>
          </div>
        </li>
      ))}
    </>
  );
};

export type PlaneOption = Record<string, unknown> & {
  id?: number | string;
  name?: string;
  value?: string;
};

/**
 * The mixed mounting-plane row: location_btn + selection mirror + select with
 * on_cabinet options (data-value carried for hood/fan_intake routing) and
 * internal-wall options (data-position="on_internal_wall").
 */
export const ComplexMountingPlaneRow = ({
  api,
  cabinetPlanes,
  internalWalls,
  selectionText,
  selectedValue,
  locationBtnNonClickable,
  selectClass = 'position_field',
  selectId,
  planeLabelRequired = false,
}: {
  api: AccessoryFormApi;
  cabinetPlanes: PlaneOption[];
  internalWalls?: InternalWallOption[];
  /** resolved selection mirror text (per-type dataGetter rules). */
  selectionText: string;
  /** current select value (plane id / hood id / IW id as string, or 'None'). */
  selectedValue: string;
  locationBtnNonClickable?: boolean;
  selectClass?: string;
  selectId?: string;
  /**
   * `.required` draws the `*` (common.css:29077). Of the five complex tmpls
   * only access_door marks this label (access_door form.tmpl:115); damper,
   * hood, louver and opening all carry a bare `class="label "`, so the default
   * is off — the same per-type opt-in MountingSection already models.
   */
  planeLabelRequired?: boolean;
}) => {
  const pos = api.position;
  const planeId = pos ? Number(pos.get('mounting_plane_id')) : null;
  return (
    <li>
      <div className="mounting-plane">
        <div
          className={`checkboxes-group mounting-btn${
            planeId != null && planeId > 6 ? ' non-clickable' : ''
          }`}
        >
          <button
            type="button"
            className={`icon-mounting-1 btn-location${
              locationBtnNonClickable ? ' non-clickable' : ''
            }`}
            name="location_btn"
            onClick={() => {
              if (!locationBtnNonClickable) api.openMountingPopup();
            }}
          />
        </div>
        <span className="selection">{selectionText ? <span>{selectionText}</span> : null}</span>
        <select
          id={selectId}
          name="mounting_plane_id"
          className={selectClass}
          required
          data-placeholder=""
          value={selectedValue}
          onChange={(e) => {
            const opt = e.currentTarget.options[e.currentTarget.selectedIndex];
            api.commitMountingPlane(
              e.currentTarget.value,
              opt?.dataset.position,
              opt?.dataset.value
            );
          }}
        >
          <option value="None" className="hide">
            - -
          </option>
          {cabinetPlanes.map((p) => (
            <option
              key={`c_${String(p.id)}`}
              value={String(p.id)}
              data-position="on_cabinet"
              data-value={String(p.value ?? '')}
              data-name={String(p.name ?? '')}
            >
              {String(p.name ?? '')}
            </option>
          ))}
          {(internalWalls ?? []).map((iw) => (
            <option
              key={`iw_${String(iw.id)}`}
              value={String(iw.id)}
              data-position="on_internal_wall"
              data-value={String(iw.value ?? '')}
            >
              {iw.name}
            </option>
          ))}
        </select>
        <span className={`label${planeLabelRequired ? ' required' : ''}`}>Mounting | Plane</span>
      </div>
    </li>
  );
};

/** Resolve the displayed plane row for the per-type dataGetter contract. */
export function resolveComplexPlane(
  api: AccessoryFormApi,
  internalWalls: InternalWallOption[] | undefined,
  cabinetPlanes: PlaneOption[]
): { planeValue: string | null; iwPlane: string | null; planeName: string } {
  const pos = api.position;
  if (!pos) return { planeValue: null, iwPlane: null, planeName: '' };
  const planeId = Number(pos.get('mounting_plane_id'));
  if (!planeId) return { planeValue: null, iwPlane: null, planeName: '' };
  const mountingType = pos.mountingType;
  // IW edge rows render when mountingType is on_internal_wall OR (fallback,
  // 6e2b verifier) when the position model was constructed on_cabinet but the
  // fetched plane id actually matches a section internal wall — mountingType
  // is frozen at construction (positionModel.ts:88) and not re-derived after
  // fetch, so a damper saved on an IW would otherwise lose its To Bottom/Left
  // Edge rows (probe k). Resolving by id keeps the on_cabinet path intact.
  const iwById = (internalWalls ?? []).find((el) => el.id == planeId);
  if (mountingType === 'on_internal_wall' || iwById) {
    return {
      planeValue: null,
      iwPlane: iwById ? String(iwById.plane ?? '') : null,
      planeName: iwById ? iwById.name : '',
    };
  }
  const plane = cabinetPlanes.find(
    (p) => p.id == planeId && !/^internal_wall_[a-z]+/.test(String(p.value ?? ''))
  );
  return {
    planeValue: plane ? String(plane.value ?? '') : null,
    iwPlane: null,
    planeName: plane ? String(plane.name ?? '') : '',
  };
}
